import { useState } from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Eye, EyeOff, AtSign } from 'lucide-react';
import { useAuthStore } from '@/stores/auth';
import { useAuthorizationStore, ModulePermission } from '@/stores/authorization';

const EMPTY_MODULES: ModulePermission[] = [];
import { useToast } from '@/hooks/use-toast';
import LogoLight from '@/assets/images/logo-icone-light.svg';
import LogoDark from '@/assets/images/logo-icone-dark.svg';
import { useEffect, useRef } from 'react';
import { LanguageSelect } from '@/components/ui/language-select';
import { useUIStore } from '@/stores/ui';
import LabelInput from '@/components/Input/LabelInput';
import { Input } from '@/components/ui/input';

export const Login = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const { toast } = useToast();
  const { login, isAuthenticated, isLoading, user } = useAuthStore();
  const { currentLanguage, setLanguage, currentTheme } = useUIStore();

  const languages = [
    { code: 'pt', name: 'Português', flag: '🇧🇷' },
    { code: 'en', name: 'English', flag: '🇺🇸' },
    { code: 'es', name: 'Español', flag: '🇪🇸' },
  ];

  const [formData, setFormData] = useState({
    user: '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const hasWelcomed = useRef(false);

  useEffect(() => {
    if (isAuthenticated && user?.name && !hasWelcomed.current) {
      // toast({
      //   title: `${t('common.welcome')}, ${user.name}!`,
      //   variant: 'info',
      // });
      hasWelcomed.current = true;
    }
    if (!isAuthenticated) {
      hasWelcomed.current = false;
    }
  }, [isAuthenticated, user, toast, t]);

  // Module-aware redirect (uses hooks, not getState during render)
  const modules = useAuthorizationStore((s) => s.payload?.modules ?? EMPTY_MODULES);
  const selectedModuleKey = useAuthorizationStore((s) => s.selectedModuleKey);

  if (isAuthenticated) {
    if (modules.length === 1) {
      // setSelectedModule already called by setPermissionToken; navigate to default_route
      return <Navigate to={modules[0].default_route || '/home'} replace />;
    }
    if (modules.length > 1) {
      if (selectedModuleKey) {
        const mod = modules.find((m) => m.key === selectedModuleKey);
        return <Navigate to={mod?.default_route || '/modules'} replace />;
      }
      return <Navigate to="/modules" replace />;
    }
    const from = location.state?.from?.pathname || '/home';
    return <Navigate to={from} replace />;
  }

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.user) {
      newErrors.user = t('auth.required_field');
    } else if (!/^[a-zA-Z0-9._]+$/.test(formData.user)) {
      newErrors.user = t('auth.invalid_user');
    }

    if (!formData.password) {
      newErrors.password = t('auth.required_field');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      await login(formData.user, formData.password);
    } catch (error) {
      toast({
        title: t('auth.invalid_credentials'),
        variant: 'destructive',
      });
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  return (
    <div className="min-h-screen flex flex-row-reverse bg-primary">
      {/* Right side - Login Form */}
      <div className="flex-[1.3] flex items-center justify-center">
        <div className="w-full h-full bg-background p-4 rounded-l-3xl flex items-center justify-center px-8 shadow-lg dark:shadow-none">
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            className="w-full max-w-lg"
          >
            {/* Logo and Title */}
            <div className="text-center mb-8">
              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="text-4xl font-bold text-foreground mb-4"
              >
                {t('auth.welcome')}
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="text-card-foreground text-sm"
              >
                {t('auth.sign_in_subtitle')}
              </motion.p>
            </div>

            {/* Login Form */}
            <motion.form
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              onSubmit={handleSubmit}
              className="space-y-4"
            >
              {/* user Field */}
              <div className="space-y-2">
                <LabelInput
                  htmlFor="user"
                  title={t('common.user') || 'Usuário'}
                />
                <div className="relative">
                  <Input
                    type="user"
                    value={formData.user}
                    onChange={(e) => handleInputChange('user', e.target.value)}
                    className={`
                    h-16 bg-transparent
                    ${errors.user ? 'border-destructive dark:border-red-500' : 'border-border '}
                  `}
                    placeholder={t('auth.input_user')}
                    disabled={isLoading}
                  />
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                    <AtSign className="h-5 w-5 text-muted-foreground dark:text-gray-400" />
                  </div>
                </div>
                {errors.user && (
                  <motion.p
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-sm text-destructive dark:text-red-400"
                  >
                    {errors.user}
                  </motion.p>
                )}
              </div>

              {/* Password Field */}
              <div className="space-y-2">
                <LabelInput
                  htmlFor="password"
                  title={t('common.password') || 'Senha'}
                />
                <div className="relative">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e) =>
                      handleInputChange('password', e.target.value)
                    }
                    className={`h-16 bg-transparent
                    ${errors.password ? 'border-destructive dark:border-red-500' : 'border-border '}
                  `}
                    placeholder={t('auth.input_password')}
                    disabled={isLoading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    disabled={isLoading}
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5 text-muted-foreground dark:text-gray-400" />
                    ) : (
                      <Eye className="h-5 w-5 text-muted-foreground dark:text-gray-400" />
                    )}
                  </button>
                </div>
                {errors.password && (
                  <motion.p
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-sm text-destructive dark:text-red-400"
                  >
                    {errors.password}
                  </motion.p>
                )}
              </div>
              <label className="text-sm font-medium text-foreground dark:text-gray-200 flex justify-end">
                <Link
                  className="pl-2 text-foreground"
                  to="/session/forgot-password"
                >
                  {t('auth.forgot_password')}
                </Link>
              </label>

              {/* Submit Button */}
              <motion.button
                type="submit"
                disabled={isLoading}
                className="btn-primary w-full py-3 px-4 rounded-lg font-medium text-white dark:bg-blue-600 dark:text-white disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2 mt-6"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                {isLoading ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <>
                    <span>{t('common.login')}</span>
                  </>
                )}
              </motion.button>
              {/* <label className="text-sm text-card-foreground flex justify-center">
                {t('auth.no_account')}{' '}
                <Link className="pl-2 text-foreground" to="/auth/register">
                  {t('auth.create_account')}
                </Link>
              </label> */}
            </motion.form>
          </motion.div>
        </div>
      </div>

      {/* Left side - Icon only */}
      <div className="hidden lg:flex flex-[0.7] relative z-10">
        <div className="absolute top-8 left-8">
          <motion.img
            initial={{ opacity: 0, scale: 0.7 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7 }}
            src={currentTheme === 'dark' ? LogoDark : LogoLight}
            alt="Logo"
            className="w-20 h-20"
          />
          <div className="fixed bottom-6 right-6 z-50">
            <LanguageSelect
              languages={languages}
              currentLanguage={currentLanguage}
              onLanguageChange={setLanguage}
              collapsed={true}
              position="top"
              align="right"
            />
          </div>
        </div>
      </div>
    </div>
  );
};
